import { z } from 'zod';
import { AccountCode, CcyCodeSchema, IsoDate, MoneyStr } from './common.js';

// ─── Внешний API реальных остатков по чатам ──────────────────────────────────
//
// GET <apiUrl> (Bearer-токен) →
//   { as_of: "YYYY-MM-DD",
//     chats: [ { id, title, balances: [ { asset, amount } ] } ] }
//
// - `id` — Telegram chat id в формате -100… (совпадает с
//   reference.accounts.telegram_chat_id) — по нему матчим на аккаунт.
// - `asset` — код валюты в нижнем регистре (rub/usdt/usd/eur/cny). Апперкейс даёт
//   валидный CcyCode; неизвестные коды сервис отбрасывает.
// - `amount` — знаковая строка (бывает отрицательной и нулевой).
//
// Схема намеренно нестрогая (passthrough-подобная): валидируем только то, что
// используем, чтобы новые поля апстрима не ломали парсинг.

export const ExternalChatBalance = z.object({
  asset: z.string(),
  amount: z.string(),
});

export const ExternalChat = z.object({
  id: z.number().int(),
  // Апстрим иногда отдаёт null/отсутствует — не роняем весь ответ ради одного
  // чата, маппер подставит String(id) как fallback-заголовок.
  title: z.string().nullish(),
  balances: z.array(ExternalChatBalance),
});

export const ExternalChatBalancesResponse = z.object({
  as_of: z.string(),
  chats: z.array(ExternalChat),
});
export type ExternalChatBalancesResponse = z.infer<typeof ExternalChatBalancesResponse>;

// ─── DTO наружу (в панель) ───────────────────────────────────────────────────

/** Один актив чата: код валюты (уже апперкейс) + нормализованная сумма. */
export const ChatBalanceAmount = z.object({
  ccy: CcyCodeSchema,
  amount: MoneyStr,
  /**
   * Когда именно эта валюта последний раз обновлялась источником (ISO).
   * Заполняется только для source='telegram' — в `telegram.chat_balances`
   * время хранится по каждой валюте отдельно (upsert (chat_id, ccy)).
   * Для source='api'/'tron' — null (там свежесть известна только на уровне
   * всего чата/ответа, см. `ChatBalanceEntry.updatedAt`).
   */
  updatedAt: z.string().nullable().default(null),
});
export type ChatBalanceAmount = z.infer<typeof ChatBalanceAmount>;

/**
 * Источник истины по остаткам КОНКРЕТНОГО чата. Учётных контуров два — внешний
 * API остатков и разбор сообщений листенером, — и когда чат живёт в обоих,
 * решает оператор: автоматике неоткуда знать, какой контур хозяин чата.
 *
 * - `auto` — API приоритетнее (историческое поведение, дефолт для всех чатов);
 * - `api` — только API; телеграмные цифры игнорируются, даже если API про чат
 *   ничего не знает (чат тогда просто пустой — осознанный выбор «не верить»);
 * - `telegram` — только разбор сообщений; запись API по этому чату отбрасывается.
 */
export const ChatBalanceSource = z.enum(['auto', 'api', 'telegram']);
export type ChatBalanceSource = z.infer<typeof ChatBalanceSource>;

/** Остатки одного чата + связанный аккаунт (если найден по telegram_chat_id). */
export const ChatBalanceEntry = z.object({
  chatId: z.string(), // "-1002743864733" (bigint как строка — JSON-safe)
  title: z.string(),
  accountCode: AccountCode.nullable(),
  accountKind: z.enum(['client', 'executor']).nullable(),
  // true — чат с обратной знаковой моделью: суммы в `balances` УЖЕ
  // инвертированы сервером (см. accounts.invert_balance). Чисто индикатор
  // для UI, пересчитывать на клиенте ничего не нужно.
  inverted: z.boolean(),
  // Источник строки: внешний API остатков, распарсенные листенером
  // сообщения (telegram.chat_balances) или опрошенный TRON-кошелёк
  // ('tron' — USDT-баланс нашего кошелька (TronScan), chatId = адрес).
  // При пересечении chat_id исход решает `balanceSource` (см. выше).
  source: z.enum(['api', 'telegram', 'tron']),
  // Настройка чата (`telegram.listening_chats.balance_source`), а НЕ то, откуда
  // цифры пришли фактически — фактический источник в `source` выше.
  // null — чата нет среди прослушиваемых, спорящих источников у него быть не
  // может и настройка неприменима (так же у TRON-кошельков).
  balanceSource: ChatBalanceSource.nullable(),
  // Для source='telegram' — max(updated_at) по валютам чата (ISO);
  // для 'api' — null (свежесть = fetchedAt всего ответа).
  updatedAt: z.string().nullable(),
  balances: z.array(ChatBalanceAmount),
});
export type ChatBalanceEntry = z.infer<typeof ChatBalanceEntry>;

/**
 * Ответ прокси. Состояния для UI:
 * - `configured=false` → источник не настроен (пустой стейт с подсказкой админу).
 * - `configured=true, error!=null, stale=false` → апстрим недоступен/отдал не то,
 *   данных нет (баннер, пустой список).
 * - `configured=true, error!=null, stale=true` → апстрим недоступен, но показываем
 *   сохранённые данные из последнего снапшота + баннер об устаревании.
 * - `configured=true, error=null` → `chats`/`asOf` заполнены live-данными.
 */
export const ChatBalancesResponse = z.object({
  configured: z.boolean(),
  asOf: IsoDate.nullable(),
  chats: z.array(ChatBalanceEntry),
  error: z.string().nullable(),
  // Данные восстановлены из последнего снапшота (live-запрос не удался).
  stale: z.boolean(),
  // ISO-момент РЕАЛЬНЫХ данных: live → время ответа апстрима, stale →
  // taken_at снапшота, источник не настроен/данных нет → null.
  fetchedAt: z.string().nullable(),
});
export type ChatBalancesResponse = z.infer<typeof ChatBalancesResponse>;

/** Тело PUT /chat-balances/chat/:chatId/source. */
export const UpdateChatBalanceSourceInput = z.object({ source: ChatBalanceSource });
export type UpdateChatBalanceSourceInput = z.infer<typeof UpdateChatBalanceSourceInput>;

// ─── Конфиг источника (URL + Bearer-токен + вкл) ─────────────────────────────

/** Конфиг для UI — БЕЗ токена, только флаг `tokenSet`. */
export const ChatBalancesConfigDto = z.object({
  enabled: z.boolean(),
  apiUrl: z.string().nullable(),
  transactionsApiUrl: z.string().nullable(),
  // URL архивного API (лента сообщений чатов, тот же хост/токен). null →
  // модуль archive выключен (GET /archive/* отдают configured=false).
  archiveApiUrl: z.string().nullable(),
  tokenSet: z.boolean(),
  updatedAt: z.string().nullable(),
});
export type ChatBalancesConfigDto = z.infer<typeof ChatBalancesConfigDto>;

/** apiToken: строка → перешифровать; null → очистить; отсутствует → не менять. */
export const UpdateChatBalancesConfigInput = z.object({
  enabled: z.boolean().optional(),
  apiUrl: z.string().url().nullish(),
  transactionsApiUrl: z.string().url().nullish(),
  archiveApiUrl: z.string().url().nullable().optional(),
  apiToken: z.string().min(1).nullish(),
});
export type UpdateChatBalancesConfigInput = z.infer<typeof UpdateChatBalancesConfigInput>;

// ─── История снапшотов остатков (см. chat-balances/snapshot.ts) ─────────────

export const HistoryRange = z.enum(['24h', '7d', '30d', '90d', 'all']);
export type HistoryRange = z.infer<typeof HistoryRange>;

/** Сводная точка истории: агрегаты снапшота в USDT. ts — ISO timestamptz. */
export const HistoryPoint = z.object({
  ts: z.string(),
  posUsdt: MoneyStr, // «мы должны» (≥ 0)
  negUsdt: MoneyStr, // «нам должны» (signed ≤ 0)
  netUsdt: MoneyStr,
  // Сумма USDT на TRON-кошельках в точке (в pos/neg/net не входит).
  walletsUsdt: MoneyStr,
  chatsCount: z.number().int(),
  // Позиция (wallets − net) по ВСЕМ снапшотам бакета, а не только по
  // последнему: медиана — устойчивый уровень (операция «в пути», пойманная
  // одним снапшотом, её не сдвигает), min/max — коридор разброса внутри бакета.
  positionMedianUsdt: MoneyStr,
  positionMinUsdt: MoneyStr,
  positionMaxUsdt: MoneyStr,
});
export type HistoryPoint = z.infer<typeof HistoryPoint>;

export const ChatBalancesHistory = z.object({
  // Размер бакета, которым агрегирован ответ: '15m'|'1h'|'4h'|'12h'|'1d'.
  bucket: z.string(),
  points: z.array(HistoryPoint),
});
export type ChatBalancesHistory = z.infer<typeof ChatBalancesHistory>;

export const CcySeriesPoint = z.object({ ts: z.string(), amount: MoneyStr });
export type CcySeriesPoint = z.infer<typeof CcySeriesPoint>;

/** Суммарный остаток по каждой валюте (в родной валюте) по бакетам. */
export const CcyHistory = z.object({
  bucket: z.string(),
  ccys: z.array(z.object({ ccy: CcyCodeSchema, points: z.array(CcySeriesPoint) })),
});
export type CcyHistory = z.infer<typeof CcyHistory>;

export const ChatSeriesPoint = z.object({ ts: z.string(), netUsdt: MoneyStr });
export type ChatSeriesPoint = z.infer<typeof ChatSeriesPoint>;

/** Net USDT по каждому чату по бакетам — для спарклайнов и топа изменений. */
export const ByChatHistory = z.object({
  bucket: z.string(),
  chats: z.array(
    z.object({
      chatId: z.string(),
      title: z.string(),
      // У чата один источник по построению (см. ChatBalanceEntry.source) —
      // нужен, чтобы вторичные представления (Movers) могли исключить кошельки.
      source: z.enum(['api', 'telegram', 'tron']),
      points: z.array(ChatSeriesPoint),
    }),
  ),
});
export type ByChatHistory = z.infer<typeof ByChatHistory>;

/** История одного чата: net в USDT + линии по валютам (в родной валюте). */
export const ChatHistoryDetail = z.object({
  bucket: z.string(),
  title: z.string(),
  net: z.array(ChatSeriesPoint),
  ccys: z.array(z.object({ ccy: CcyCodeSchema, points: z.array(CcySeriesPoint) })),
});
export type ChatHistoryDetail = z.infer<typeof ChatHistoryDetail>;

// ─── Устойчивые точки и сверка дня (см. chat-balances/daily.ts) ─────────────

/** Часовой пояс бизнес-суток для дневной истории и сверки дня. */
export const BUSINESS_TZ = 'Europe/Moscow';

/**
 * Точка позиции на границе интервала. `stable=false` — в окне перед границей
 * не нашлось снапшота, близкого к медиане окна (шла операция «в пути»), взят
 * последний снапшот как есть: цифре на этой границе доверять осторожно.
 */
export const StablePoint = z.object({
  ts: z.string(),
  positionUsdt: MoneyStr,
  stable: z.boolean(),
});
export type StablePoint = z.infer<typeof StablePoint>;

export const DailyPositionDay = z.object({
  date: IsoDate,
  open: StablePoint.nullable(),
  close: StablePoint.nullable(),
  // Разброс сырых снапшотов внутри суток.
  minUsdt: MoneyStr.nullable(),
  maxUsdt: MoneyStr.nullable(),
  medianUsdt: MoneyStr.nullable(),
  samples: z.number().int(),
});
export type DailyPositionDay = z.infer<typeof DailyPositionDay>;

export const DailyPositionHistory = z.object({
  tz: z.string(),
  days: z.array(DailyPositionDay),
});
export type DailyPositionHistory = z.infer<typeof DailyPositionHistory>;

export const DailyPositionQuery = z.object({
  days: z.coerce.number().int().min(1).max(366).default(30),
});

/** Вклад одного чата/кошелька в изменение позиции за интервал. */
export const ReconciliationItem = z.object({
  chatId: z.string(),
  title: z.string(),
  source: z.enum(['api', 'telegram', 'tron']),
  // Вклад в позицию, USDT: для чата — −Δ(остаток) по курсу конца интервала,
  // для кошелька — +Δ(остаток).
  deltaUsdt: MoneyStr,
  ccys: z.array(z.object({ ccy: z.string(), deltaAmount: MoneyStr })),
});
export type ReconciliationItem = z.infer<typeof ReconciliationItem>;

export const DayReconciliation = z.object({
  date: IsoDate,
  tz: z.string(),
  from: StablePoint.nullable(),
  to: StablePoint.nullable(),
  deltaUsdt: MoneyStr.nullable(),
  items: z.array(ReconciliationItem),
  // Остаток разницы, не разложенный по чатам: курсовая переоценка (курс
  // поменялся, суммы — нет) плюс чаты без курса.
  revaluationUsdt: MoneyStr.nullable(),
  // Непарные движения журнала за интервал (см. balance-changes/matching.ts).
  unmatched: z.object({
    count: z.number().int(),
    sumAbsUsdt: MoneyStr,
    minUsdt: z.number(),
    truncated: z.boolean(),
  }),
});
export type DayReconciliation = z.infer<typeof DayReconciliation>;

export const DayReconciliationQuery = z.object({ date: IsoDate.optional() });
